/**
 * src/services/reportsService.js
 *
 * All API calls for the Reports module.
 * Uses the shared axios instance from src/lib/api.js
 * so the Bearer token is attached automatically.
 */

import api from "../lib/api";

const reportsService = {
  // ── Overview card stats ────────────────────────────────────────────
  // GET /api/reports/overview
  getOverview: async () => {
    const { data } = await api.get("/reports/overview");
    return data; // { success, data: { totalProjects, totalTasks, ... } }
  },

  // ── Project list for the dropdown ─────────────────────────────────
  // GET /api/reports/projects
  getProjects: async () => {
    const { data } = await api.get("/reports/projects");
    return data; // { success, data: [ { _id, name, ... }, ... ] }
  },

  // ── Project detail tabs ───────────────────────────────────────────
  // GET /api/reports/projects/:projectId/summary
  getProjectSummary: async (projectId) => {
    const { data } = await api.get(`/reports/projects/${projectId}/summary`);
    return data;
  },

  // GET /api/reports/projects/:projectId/status?page=1&limit=5
  getProjectStatus: async (projectId, page = 1, limit = 5) => {
    const { data } = await api.get(
      `/reports/projects/${projectId}/status`,
      { params: { page, limit } }
    );
    return data;
  },

  // GET /api/reports/projects/:projectId/timesheet?page=1&limit=5&startDate=&endDate=
  getProjectTimesheet: async (projectId, page = 1, limit = 5, startDate = "", endDate = "") => {
    const { data } = await api.get(
      `/reports/projects/${projectId}/timesheet`,
      { params: { page, limit, startDate, endDate } }
    );
    return data;
  },

  // GET /api/reports/projects/:projectId/risk?page=1&limit=5
  getProjectRisk: async (projectId, page = 1, limit = 5) => {
    const { data } = await api.get(
      `/reports/projects/${projectId}/risk`,
      { params: { page, limit } }
    );
    return data;
  },

  // GET /api/reports/projects/:projectId/tasks/:taskId
  getTaskDetail: async (projectId, taskId) => {
    const { data } = await api.get(
      `/reports/projects/${projectId}/tasks/${taskId}`
    );
    return data;
  },

  // GET /api/reports/projects/:projectId/tasks/:taskId/timesheet
  getTaskTimesheetDetail: async (projectId, taskId, startDate = "", endDate = "") => {
    const { data } = await api.get(
      `/reports/projects/${projectId}/tasks/${taskId}/timesheet`,
      { params: { startDate, endDate } }
    );
    return data;
  },

  // GET /api/reports/projects/:projectId/download?tab=status
  downloadProjectReport: async (projectId, tab = "status", startDate = "", endDate = "") => {
    const response = await api.get(
      `/reports/projects/${projectId}/download`,
      {
        params: { tab, startDate, endDate },
        responseType: "blob", // important for file downloads
      }
    );
    return response; // caller handles blob → download link
  },

  // ── Leave report ──────────────────────────────────────────────────
  // GET /api/reports/leave/resources
  getLeaveResources: async () => {
    const { data } = await api.get("/reports/leave/resources");
    return data; // { success, data: [ { _id, name, email, ... } ] }
  },

  // GET /api/reports/leave?year=2026&page=1&limit=5
  getLeaveReport: async (year = new Date().getFullYear(), page = 1, limit = 5, userId = "") => {
    const { data } = await api.get("/reports/leave", {
      params: { year, page, limit, ...(userId ? { userId } : {}) },
    });
    return data;
  },

  // GET /api/reports/leave/download?year=2026&userId=...
  downloadLeaveReport: async (year = new Date().getFullYear(), userId = "") => {
    const response = await api.get("/reports/leave/download", {
      params: { year, ...(userId ? { userId } : {}) },
      responseType: "blob",
    });
    return response;
  },
};

export default reportsService;